import type { OptimizerAuditV2Record } from '../../types/audit.types';

/**
 * Some optimizer runs persist an inconsistent input snapshot: a sub-account's
 * recorded market value equals (or is below) its cash, so investable = 0 and
 * every `currentWeight` is forced to 0 — yet the run still reports sells. That
 * combination makes the grid render a phantom full liquidation (every position
 * reconstructs to a $0 end state) even though the account actually holds
 * securities.
 *
 * Detecting it lets the drawer warn and point the advisor to the portfolio page
 * instead of showing misleading zeros.
 */
export const isAuditSnapshotUnreliable = (
  audit?: OptimizerAuditV2Record,
): boolean => {
  if (!audit?.subAccountResults?.length) {
    return false;
  }

  return audit.subAccountResults.some((result) => {
    const marketValueCents = result.inputMarketValueCents ?? 0;
    const cashCents = result.inputCashCents ?? 0;
    const investableCents = marketValueCents - cashCents;
    const soldCents = result.totalSellCents ?? 0;

    // No investable market value, yet the run sold positions — the recorded
    // market value can't include the holdings it traded, so the pre-trade
    // snapshot is untrustworthy.
    return investableCents <= 0 && soldCents > 0;
  });
};
